import ExcelJS from 'exceljs';
import { calculateFillRatio } from '../stowage/stowageOperation/stowageOperationUtils';
import { getPeakCommingledVolume } from '../stowage/stowageOperation/commingledVolumeUtils';
import { calculateVolume } from '../stowage/stowageOperation/volumeUtils';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import Decimal from 'decimal.js';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import theme from 'src/theme';
import { prettifyDate } from 'odfjell-date-time-utils/dateTimeFormatter';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { getCargoTemperature } from '../stowage/stowageOperation/cargoTemperatureUtils';

/**
 * Returns the ordinal suffix for a number (1st, 2nd, 3rd, etc.)
 * @param number - The number to get the suffix for
 * @returns The ordinal suffix
 */
export const getOrdinalSuffix = (number: number): string => {
    if (number % 100 >= 11 && number % 100 <= 13) {
        return 'th';
    }

    switch (number % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};

export const getLetterForNumber = (num: number): string => {
    let result = '';
    let n = num;

    while (n >= 0) {
        result = String.fromCharCode(65 + (n % 26)) + result;
        n = Math.floor(n / 26) - 1;
        if (n < 0) break;
    }

    return result;
};

export const rgbToHex = (rgb: string): string => {
    const match = rgb.match(/\d+/g);
    if (!match) return '000000';
    const [r, g, b] = match;
    return `${(+r).toString(16).padStart(2, '0')}${(+g).toString(16).padStart(2, '0')}${(+b).toString(16).padStart(2, '0')}`;
};

export const setCargoColor = (cell: ExcelJS.Cell, color: string) => {
    const argbColor = `FF${color}`;

    cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: argbColor },
        bgColor: {
            argb: 'FF000000',
        },
    };
};

export const getFormattedDate = (date: Date): string => {
    return prettifyDate(date);
};

export const getFormattedFillRatio = (weight: Decimal, stowagePlan: StowagePlan, cargo: CargoContent, tank: Tank): number | null => {
    const cargoTemperature = getCargoTemperature(stowagePlan, cargo.cargo);
    const volume = getPeakCommingledVolume(stowagePlan, cargo) ?? calculateVolume(weight, cargo.cargo, cargoTemperature);

    const fillRatioString = calculateFillRatio(volume, tank).toFixed(4);
    const fillRatioNumber = parseFloat(fillRatioString);
    return fillRatioNumber > 0 ? fillRatioNumber : null;
};

export const setHeaderStyle = (cell: ExcelJS.Cell) => {
    // get color which is in rgb format (rgb(x, y, z)), change to hex format, then to argb
    const headerColorHex = rgbToHex(theme.colors.background.odfjellPanel.menuBackground);
    const headerColorArgb = `FF${headerColorHex}`;

    cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: headerColorArgb },
    };
    cell.font = {
        bold: true,
        color: { argb: 'FFFFFFFF' },
    };
    cell.alignment = {
        vertical: 'middle',
        horizontal: 'center',
    };
};

export const getCharterersText = (charterers: string[]): string => {
    const maxLength = 70;
    if (charterers && charterers.length > 0) {
        let charterersText = charterers.join(', ');
        if (charterersText.length > maxLength) {
            charterersText = charterersText.substring(0, maxLength) + '...';
        }
        return `Charterer(s): ${charterersText}`;
    }
    return '';
};
